import { useCallback, useMemo, useRef } from "react";

// The text of the server's refusal of a save built from an older version (see
// STALE_SAVE_MESSAGE in backend/career/schema.py).
export const staleSaveMessage = "changed after the page loaded it";

export const isStaleSaveError = (error: unknown) =>
  String((error as Error | null)?.message ?? "").includes(staleSaveMessage);

// Which saved version a page's copy is built from, for its saves to send: the
// server refuses one built from an older version, so a page that missed a
// change (Topicflow AI's, another tab's, another admin's) can't write its old
// values over it.
//
// Also every version the page has seen, loaded or returned by its own saves. A
// refetch that brings any other is a change made elsewhere. Versions are
// hashes, so they can't be put in order: this is how a refetch sent before the
// page's own save landed is told apart, as a version already seen.
const useSavedVersion = (initial?: string | null) => {
  const baseRef = useRef<string | null>(initial ?? null);
  const seenRef = useRef(new Set<string>(initial ? [initial] : []));
  // The page's copy now matches this version.
  const learn = useCallback((version: string) => {
    baseRef.current = version;
    seenRef.current.add(version);
  }, []);
  // Noticed, but the page's copy stays as it is (it has unsaved edits).
  const see = useCallback((version: string) => {
    seenRef.current.add(version);
  }, []);
  const isNew = useCallback(
    (version: string) => !seenRef.current.has(version),
    [],
  );
  return useMemo(() => ({ baseRef, learn, see, isNew }), [learn, see, isNew]);
};

export default useSavedVersion;
